import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/InstructorFormativeJourney/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Paper, Stack, styled, Text, ViewMore } from 'shell/components';
import { getRubricMaxPoints, getRubricTotalSuggestedPoints } from 'utils/helper';

const getDraftNumberText = (index) => {
    return String(index + 1);
};

const getUploadDateText = (submission, t) => {
    const timestamp = submission?.created_on;
    if (!timestamp) return '--';
    return t('common:ui.format-date-long-time', { val: timestamp });
};

const getProficiencyLevelText = (submission, assignment) => {
    if (!submission) return '--';
    return `${getRubricTotalSuggestedPoints(assignment, submission)}/${getRubricMaxPoints(assignment.rubric.criteria)}`;
};

const getFeedbackInteractionsText = (feedbackInteractionsByDraft, draftNumber) => {
    if (!feedbackInteractionsByDraft) return '--';

    return String(feedbackInteractionsByDraft?.[draftNumber] ?? 0);
};

const getPerformanceText = (submission) => {
    return submission?.ai?.ai_performance_summary_instructor ?? '--';
};

const DraftCard = ({ index }) => {
    const { currentAssignment } = useApp();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const { currentStudentSubmissions, feedbackInteractionsByDraft, onClickViewFormative, onClickViewSubmission } =
        useContext(PageContext);

    const currentStudentSubmission = currentStudentSubmissions[index];

    return (
        <Stack sx={{ width: device !== 'desktop' ? '100%' : '260px' }} spacing={5}>
            <CustomPaper sx={{ alignSelf: 'center', borderRadius: '40px', width: 'fit-content' }}>
                <Text weight={'medium'}>
                    {t('draft-number', 'Draft {count}', { count: getDraftNumberText(index) })}
                </Text>
            </CustomPaper>
            <CustomPaper>
                <Stack spacing={5}>
                    <Stack
                        direction={device === 'desktop' ? 'row' : 'column'}
                        justifyContent={'space-between'}
                        spacing={2}
                    >
                        <Text sx={{ whiteSpace: 'nowrap' }} color={'light'}>
                            {t('draft-number', 'Draft {count}', { count: getDraftNumberText(index) })}
                        </Text>
                        <Text sx={{ textAlign: 'right' }} color={'light'}>
                            {getUploadDateText(currentStudentSubmission, t)}
                        </Text>
                    </Stack>
                    <Button
                        disabled={!currentStudentSubmission}
                        variant={'secondary'}
                        onClick={() => onClickViewSubmission(currentStudentSubmission.id)}
                    >
                        {t('feedback-section.view-feedback', 'View Feedback')}
                    </Button>
                </Stack>
            </CustomPaper>
            <CustomPaper>
                <Stack spacing={3}>
                    <Text weight={'medium'}>
                        {t('formative-feedback.instructor-insights', 'For Instructor Insights')}
                    </Text>
                    <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                        <Stack>
                            <Text weight={'medium'}>
                                {t('formative-feedback.proficiency-level', 'Proficiency Level')}
                            </Text>
                            <Text weight={'medium'}>
                                ({t('formative-feedback.not-student-shown', 'Not shown to student')})
                            </Text>
                        </Stack>
                        <Text>
                            {getProficiencyLevelText(
                                currentStudentSubmission,
                                currentStudentSubmission?.assignment_snapshot ?? currentAssignment
                            )}
                        </Text>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'}>
                        <Text weight={'medium'}>
                            {t('formative-feedback.feedback-interactions', 'Feedback interactions')}
                        </Text>
                        <Text>{getFeedbackInteractionsText(feedbackInteractionsByDraft, index + 1)}</Text>
                    </Stack>
                    <Divider />
                    <Stack
                        direction={currentStudentSubmission ? 'column' : 'row'}
                        justifyContent={'space-between'}
                        spacing={1}
                    >
                        <Text weight={'medium'}>
                            {t('formative-feedback.student-performance', 'Student Performance')}
                        </Text>
                        <ViewMore variant={'primary'}>{getPerformanceText(currentStudentSubmission)}</ViewMore>
                    </Stack>
                    <Divider />
                    <Button
                        sx={{ width: 'fit-content' }}
                        disabled={!currentStudentSubmission}
                        variant={'borderless'}
                        onClick={() => onClickViewFormative(currentStudentSubmission.id)}
                    >
                        {t('common:controls.view-parameters', 'View Parameters')}
                    </Button>
                </Stack>
            </CustomPaper>
        </Stack>
    );
};

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(3),
    };
});

export default DraftCard;
